import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';
import React, { useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useAuth } from '../../auth';
import { AttachmentRemovalDialog } from '../../components/attachment-removal-dialog';
import { ChatErrorModal } from '../../components/chat-error-modal';
import { ChatGate } from '../../components/chat-gate';
import { ChatLog } from '../../components/chat-log';
import { ChatSessionList } from '../../components/chat-session-list';
import { ContextUsageBar } from '../../components/context-usage-bar';
import { ContextWarningBanner } from '../../components/context-warning-banner';
import { DeleteChatDialog } from '../../components/delete-chat-dialog';
import { DraftSaveDialog, type DraftSaveAction } from '../../components/draft-save-dialog';
import { RenameChatDialog } from '../../components/rename-chat-dialog';
import { ScreenCapturePermissionModal } from '../../components/screen-capture-permission-modal';
import { SidebarHeader } from '../../components/sidebar-header';
import { useDebounce } from '../../hooks/use-debounce';
import {
  useArchiveChatMutation,
  useCompactContextMutation,
  useDeleteChatMutation,
  useUnarchiveChatMutation,
  useUpdateChatTitleMutation,
} from '../../hooks/use-gateway-queries';
import { RealTimeModeView } from '../../components/real-time-mode-view';
import { usePlatform } from '../../hooks/use-platform';
import { usePlatform as usePlatformContext } from '../../contexts/platform-context';
import { useRealTimeMode } from '../../hooks/use-real-time-mode';
import { useRealTimeModeMatch } from '../../stores/real-time-mode-store';
import { formatModmedContext, isAttachableMatchState } from '../../utils/format-modmed-context';
import { providerDisplayName } from '@ctrl-enter/types';
import { useAiSkills } from '../../hooks/use-ai-skills';
import { useAiSkillStore, useSelectedSkill, useAvailableSkills } from '../../stores/ai-skill-store';
import { SkillSelectorModal, SkillDynamicIsland } from '../../components/ai-skills';
import { useProgressiveSearch } from '../../hooks/use-progressive-search';
import { useScreenCapture, useScreenCapturePermissionGuard } from '../../hooks/use-screen-capture';
import { usePageCapture } from '../../hooks/use-page-capture';
import { useArchiveFilter, useArchivedCount, useS3ChatStore } from '../../stores/s3-chat-store';
import {
  useStarterPromptsDismissed,
  useUpdateUIPreferences,
} from '../../stores/system-settings-store';
import { usePinnedSessionsStore } from '../../stores/pinned-sessions-store';
import { cn } from '../../utils/cn';
import { ATTACHMENT_LIMITS } from '@ctrl-enter/tools';
import {
  handleFileUpload,
  processSelectedFiles,
  validateAndOptimizeRequestSize,
} from '../../utils/file-upload';
import { mapLocalMessagesToChatMessages } from '../../utils/message-mapper';
import {
  mapOpenAIError,
  type ErrorAction,
  type MappedError,
} from '../../utils/openai-error-mapper';
import { logDebug, logError, logReactError } from '../../utils/safe-logger';
import { toast } from '../../utils/toast';
import { estimateTotalConversationTokens } from '../../utils/token-estimation';
import { useChatViewState } from './hooks/use-chat-view-state';

const logger = createLogger('client:view:chat-view');

// Define the ref methods that can be called on ChatView
export type ChatViewRef = {
  newChat: () => Promise<void>;
  getMessageCount: () => number;
  getDraftAttachmentCount: () => number;
};

// Main ChatView component - Accepts props for state management
export const ChatView = React.forwardRef<
  ChatViewRef,
  {
    className?: string;
    showSessionPanel?: boolean;
    onClose: () => void;
    onPermissionsModalChange?: (isShown: boolean) => void;
    onGoToIntegrations?: () => void;
    onSessionSelect?: () => void;
  }
>(
  (
    {
      className,
      showSessionPanel = false,
      onClose,
      onPermissionsModalChange,
      onGoToIntegrations,
      onSessionSelect,
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const initialChatCreated = useRef(false);
    const lastCaptureTime = useRef<number>(0);
    const captureInProgress = useRef<boolean>(false);
    const lastSubmittedMessage = useRef<string>(''); // Store last submitted message for error recovery
    const screenCaptureService = useScreenCapture();
    const pageCaptureService = usePageCapture();
    const permissionGuard = useScreenCapturePermissionGuard();
    const { needsFlatDesign } = usePlatform();
    const { isSidebar } = usePlatformContext();
    // Integration management is admin-only; used to gate the RealTimeModeView
    // "Connect Enter" affordance so non-admins get the external-link fallback.
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    // Register Real-Time Mode listener — receives MatchState updates from the
    // extension (via sidepanel bridge) and writes them into the RTM store so
    // RealTimeModeView can render.
    useRealTimeMode();
    const realTimeModeMatch = useRealTimeModeMatch();

    // Bootstrap AI skills (fetches /ai-skills once, populates ai-skill-store)
    // Note: useAiSkills has no dependency on state, so it's safe here
    useAiSkills();

    // Starter prompts — dismissed permanently after first message send
    const starterPromptsDismissed = useStarterPromptsDismissed();
    const updateUIPreferences = useUpdateUIPreferences();

    // Error modal state
    const [errorModal, setErrorModal] = useState<{ isOpen: boolean; error: MappedError | null }>({
      isOpen: false,
      error: null,
    });

    // Archive filter state and actions
    const archiveFilter = useArchiveFilter();
    const archivedCount = useArchivedCount();
    const setArchiveFilter = useS3ChatStore(state => state.setArchiveFilter);
    const archiveSessionStore = useS3ChatStore(state => state.archiveSession);
    const unarchiveSessionStore = useS3ChatStore(state => state.unarchiveSession);

    // Chat action mutations
    const archiveChatMutation = useArchiveChatMutation();
    const unarchiveChatMutation = useUnarchiveChatMutation();
    const deleteChatMutation = useDeleteChatMutation();
    const updateTitleMutation = useUpdateChatTitleMutation();
    const compactContextMutation = useCompactContextMutation();

    // Dialog states
    const [deleteDialog, setDeleteDialog] = useState<{
      isOpen: boolean;
      sessionId: string;
      sessionTitle: string;
    }>({
      isOpen: false,
      sessionId: '',
      sessionTitle: '',
    });
    const [renameDialog, setRenameDialog] = useState<{
      isOpen: boolean;
      sessionId: string;
      currentTitle: string;
    }>({
      isOpen: false,
      sessionId: '',
      currentTitle: '',
    });
    const [attachmentRemovalDialog, setAttachmentRemovalDialog] = useState<{
      isOpen: boolean;
    }>({
      isOpen: false,
    });
    const [draftSaveDialog, setDraftSaveDialog] = useState<{
      isOpen: boolean;
      pendingAction: (() => void) | null;
      actionType: 'new-chat' | 'close' | null;
    }>({
      isOpen: false,
      pendingAction: null,
      actionType: null,
    });

    // Preview attachments (files waiting for user confirmation)

    const { state, actions } = useChatViewState({
      onError: errorMessage => {
        logger.error('🔔 [ChatView] Chat error:', errorMessage);

        // Map the error to a user-friendly format
        const mappedError = mapOpenAIError(errorMessage);

        // Show the enhanced error modal
        setErrorModal({ isOpen: true, error: mappedError });

        // Log error for debugging (in development) - use React-specific logging if it's a React error
        if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('hook')) {
          logReactError('Healthcare chat error', errorMessage, 'ChatView');
        } else {
          logError('Healthcare chat error', errorMessage);
        }
      },
    });

    // Per-session AI skill selection (must come after useChatViewState — needs state.sessionId)
    const sessionIdForSkill = state.sessionId || 'new';
    const selectedSkill = useSelectedSkill(sessionIdForSkill);
    const availableSkills = useAvailableSkills();
    const aiSkillStore = useAiSkillStore();
    const [isSkillSelectorOpen, setIsSkillSelectorOpen] = useState(false);

    // Search state - lives at ChatView level, tab-agnostic
    // MUST come after useChatViewState() since it needs state.sessions
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // Progressive search - searches ALL sessions regardless of tab
    const { results: searchResults, progress: searchProgress } = useProgressiveSearch({
      sessions: state.sessions, // ALL sessions (both active & archived)
      query: debouncedSearchQuery,
      enabled: debouncedSearchQuery.trim().length >= 2,
    });

    // Expose methods through ref
    useImperativeHandle(
      ref,
      () => ({
        newChat: async () => {
          logDebug('ChatView: newChat called via ref - creating new chat without screenshot');
          await actions.newChat();

          // Focus the textarea after new chat is created
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        },
        getMessageCount: () => {
          return state.currentSession?.messages?.length || 0;
        },
        getDraftAttachmentCount: () => {
          return state.draftAttachments?.length || 0;
        },
      }),
      [actions, screenCaptureService, permissionGuard, state.currentSession, state.draftAttachments]
    );

    React.useEffect(() => {
      logger.info('🔵 [ChatView] Component mounted/updated');

      if (!initialChatCreated.current) {
        initialChatCreated.current = true;
        logger.info('🔵 [ChatView] Creating initial chat');
        actions.newChat();
      } else {
        logger.info('🔵 [ChatView] Initial chat already created, skipping');
      }

      return () => {
        logger.info('🔴 [ChatView] Component unmounting');
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // ⚠️ Intentionally empty - only run once on mount (ref prevents multiple calls)

    // Notify parent when permissions modal state changes
    React.useEffect(() => {
      onPermissionsModalChange?.(permissionGuard.showPermissionModal);
    }, [permissionGuard.showPermissionModal, onPermissionsModalChange]);

    // State for last upload/capture status
    const [, setLastUploadState] = React.useState<string>('');

    const handleFilesDrop = async (files: File[]) => {
      try {
        logDebug('Processing dropped files', {
          count: files.length,
          fileNames: files.map(f => f.name),
        });

        // Use the same processing logic as file picker
        const result = await processSelectedFiles(files, state.draftAttachments, []);

        if (result.errors.length > 0) {
          // Show errors as toast notifications with enhanced messaging
          result.errors.forEach(error => {
            // Check if it's a size limit error for enhanced messaging
            if (error.includes('exceed') && error.includes('limit')) {
              toast.error('File Size Limit Exceeded', error, {
                label: 'View Limits',
                onRefresh: () => {
                  const details = result.sizeValidation;
                  if (details) {
                    const currentMB = (details.currentSizeKB / 1024).toFixed(1);
                    const limitMB = (details.limitKB / 1024).toFixed(1);
                    toast.info(
                      'Request Size Details',
                      `Current message size: ${currentMB}MB\nMaximum allowed: ${limitMB}MB\n\nTip: Try reducing image quality or removing some files.`
                    );
                  }
                },
              });
            } else {
              toast.error('Drop Error', error);
            }
          });
          setLastUploadState('');
          return; // Don't proceed if validation failed
        }

        if (result.attachments.length > 0) {
          // Add new attachments directly to draft (skip confirmation)
          const currentCount = state.draftAttachments.length;
          const availableSlots = Math.max(
            0,
            ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST - currentCount
          );
          const attachmentsToAdd = result.attachments.slice(0, availableSlots);

          if (attachmentsToAdd.length > 0) {
            // Add directly to draft attachments
            const newAttachments = attachmentsToAdd.map(a => ({
              id: a.id || randomUUID(), // Ensure ID exists
              name: a.name,
              contentType: a.contentType,
              url: a.url,
              isScreenshot: false,
              file: a.file, // Preserve File object
              fileSizeMB: a.fileSizeMB,
              estimatedTokens: a.estimatedTokens,
            }));

            // Add to draft attachments
            const existingFiles = state.draftAttachments.filter(a => !a.isScreenshot);
            actions.updateAttachments([...existingFiles, ...newAttachments]);

            // Log success and show toast
            const sizeInfo = result.sizeValidation;
            logDebug('Files dropped and added to draft', {
              count: attachmentsToAdd.length,
              fileNames: attachmentsToAdd.map(a => a.name),
              sizeValidation: sizeInfo,
            });

            // Show success toast
            const fileCount = attachmentsToAdd.length;
            if (fileCount === 1 && attachmentsToAdd[0]) {
              toast.success(
                'File Added',
                `${attachmentsToAdd[0].name} has been added to your message.`
              );
            } else {
              toast.success('Files Added', `${fileCount} files have been added to your message.`);
            }

            // Set upload state for dropped files
            setLastUploadState('uploaded');
          }

          // Warn if some files were rejected due to limit
          if (result.attachments.length > availableSlots) {
            const rejected = result.attachments.length - availableSlots;
            toast.warning(
              'File Limit Reached',
              `${rejected} file${rejected > 1 ? 's' : ''} not added. Maximum ${ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST} files allowed.`
            );
          }
        } else {
          setLastUploadState('');
        }
      } catch (uploadError) {
        logError('File drop failed', uploadError);
        toast.error(
          'Drop Failed',
          uploadError instanceof Error ? uploadError.message : 'Unknown error occurred'
        );
        setLastUploadState('');
      }
    };

    const handleUpload = async () => {
      try {
        logDebug('Opening file picker for file upload');

        // Validate against request size limits with smart optimization
        const result = await handleFileUpload(state.draftAttachments, []);

        if (result.errors.length > 0) {
          // Show errors as toast notifications with enhanced messaging
          result.errors.forEach(error => {
            // Check if it's a size limit error for enhanced messaging
            if (error.includes('exceed') && error.includes('limit')) {
              toast.error('File Size Limit Exceeded', error, {
                label: 'View Limits',
                onRefresh: () => {
                  const details = result.sizeValidation;
                  if (details) {
                    const currentMB = (details.currentSizeKB / 1024).toFixed(1);
                    const limitMB = (details.limitKB / 1024).toFixed(1);
                    toast.info(
                      'Request Size Details',
                      `Current message size: ${currentMB}MB\nMaximum allowed: ${limitMB}MB\n\nTip: Try reducing image quality or removing some files.`
                    );
                  }
                },
              });
            } else {
              toast.error('Upload Error', error);
            }
          });
          setLastUploadState('');
          return; // Don't proceed if validation failed
        }

        if (result.attachments.length > 0) {
          // Add new attachments directly to draft (skip confirmation)
          const currentCount = state.draftAttachments.length;
          const availableSlots = Math.max(
            0,
            ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST - currentCount
          );
          const attachmentsToAdd = result.attachments.slice(0, availableSlots);

          if (attachmentsToAdd.length > 0) {
            // Add directly to draft attachments
            const newAttachments = attachmentsToAdd.map(a => ({
              id: a.id || randomUUID(), // Ensure ID exists
              name: a.name,
              contentType: a.contentType,
              url: a.url,
              isScreenshot: false,
              file: a.file, // Preserve File object
              fileSizeMB: a.fileSizeMB,
              estimatedTokens: a.estimatedTokens,
            }));

            // Add to draft attachments
            const existingFiles = state.draftAttachments.filter(a => !a.isScreenshot);
            actions.updateAttachments([...existingFiles, ...newAttachments]);

            // Log success and show toast
            const sizeInfo = result.sizeValidation;
            logDebug('Files added to draft', {
              count: attachmentsToAdd.length,
              fileNames: attachmentsToAdd.map(a => a.name),
              sizeValidation: sizeInfo,
            });

            // Show success toast
            const fileCount = attachmentsToAdd.length;
            if (fileCount === 1 && attachmentsToAdd[0]) {
              toast.success(
                'File Added',
                `${attachmentsToAdd[0].name} has been added to your message.`
              );
            } else {
              toast.success('Files Added', `${fileCount} files have been added to your message.`);
            }

            // Set upload state
            setLastUploadState('uploaded');
          }

          // Warn if some files were rejected due to limit
          if (result.attachments.length > availableSlots) {
            const rejected = result.attachments.length - availableSlots;
            toast.warning(
              'File Limit Reached',
              `${rejected} file${rejected > 1 ? 's' : ''} not added. Maximum ${ATTACHMENT_LIMITS.MAX_NEW_ATTACHMENTS_PER_REQUEST} files allowed.`
            );
          }
        } else {
          setLastUploadState('');
        }
      } catch (uploadError) {
        logError('File upload failed', uploadError);
        toast.error(
          'Upload Failed',
          uploadError instanceof Error ? uploadError.message : 'Unknown error occurred'
        );
        setLastUploadState('');
      }
    };

    const handleScreenCapture = React.useCallback(async () => {
      if (!screenCaptureService.isAvailable) {
        toast.error(
          'Screen Capture Not Available',
          'Screen capture requires the browser extension. Please install it to use this feature.'
        );
        logError('Screen capture service not available - extension required');
        return;
      }

      // Check if a capture is already in progress
      if (captureInProgress.current) {
        logDebug('Manual screenshot capture blocked - capture already in progress');
        toast.warning('Capture in Progress', 'Please wait for the current screenshot to complete.');
        return;
      }

      // Debounce rapid manual captures
      const now = Date.now();
      if (now - lastCaptureTime.current < 500) {
        logDebug('Manual screenshot capture blocked - debounce period');
        return;
      }

      captureInProgress.current = true;
      lastCaptureTime.current = now;

      try {
        // Always capture a new screenshot - no toggle behavior
        await performScreenCapture();
      } finally {
        captureInProgress.current = false;
      }
    }, [screenCaptureService]);

    const performScreenCapture = async (showErrorToast = true) => {
      // Check permissions first
      if (!permissionGuard.guardScreenCaptureAction()) {
        logDebug('Screen capture blocked - no permission');
        return;
      }

      // Check if any images are currently processing - if so, don't take screenshot
      if (state.hasProcessingImages) {
        logDebug('Screen capture blocked - images are currently processing');
        if (showErrorToast) {
          toast.warning(
            'Upload in Progress',
            'Please wait for current uploads to complete before taking a screenshot.'
          );
        }
        return;
      }

      // Check if adding a screenshot would exceed limits BEFORE capturing
      // Estimate screenshot size (typical PNG screenshot is ~500KB-2MB)
      const estimatedScreenshotFile = new File([''], 'screenshot.png', {
        type: 'image/png',
      });
      Object.defineProperty(estimatedScreenshotFile, 'size', { value: 1024 * 1024 }); // 1MB estimate

      const sizeValidation = validateAndOptimizeRequestSize(
        [estimatedScreenshotFile],
        state.draftAttachments,
        []
      );

      if (!sizeValidation.canProceed) {
        if (showErrorToast) {
          toast.error(
            'Cannot Capture Screenshot',
            sizeValidation.error || 'Size validation failed'
          );
        }
        logError('Screenshot size validation failed before capture', sizeValidation.error);
        return;
      }

      // Capture screenshot
      try {
        logDebug('Starting screen capture');
        const result = await screenCaptureService.quickCapture();
        logDebug('Screen capture result', {
          success: result.success,
          hasDataUrl: !!result.dataUrl,
        });

        if (result.success && result.dataUrl) {
          // Validate actual screenshot size and apply optimizations
          const actualSizeKB = Math.round((result.dataUrl.length * 0.75) / 1024);
          const actualScreenshotFile = new File([''], 'screenshot.png', { type: 'image/png' });
          Object.defineProperty(actualScreenshotFile, 'size', { value: actualSizeKB * 1024 });

          const finalValidation = validateAndOptimizeRequestSize(
            [actualScreenshotFile],
            state.draftAttachments,
            []
          );

          if (!finalValidation.canProceed) {
            if (showErrorToast) {
              toast.error(
                'Screenshot Too Large',
                finalValidation.error || 'Screenshot size validation failed'
              );
            }
            logError('Screenshot size validation failed', {
              actualSizeKB,
              validation: finalValidation,
            });
            setLastUploadState('');
            return;
          }

          const screenShotAttachment = {
            id: randomUUID(),
            name: `screen_capture_${result.timestamp}.png`,
            contentType: 'image/png',
            url: result.dataUrl,
            isScreenshot: true,
          };

          logDebug('Updating screenshots with new capture', {
            attachmentName: screenShotAttachment.name,
            currentScreenshots: state.draftScreenshots.length,
          });

          // Append the new screenshot to existing screenshots
          actions.updateScreenshots([...state.draftScreenshots, screenShotAttachment]);

          logDebug('Screenshot captured successfully', {
            timestamp: result.timestamp,
            sizeKB: actualSizeKB,
            name: screenShotAttachment.name,
          });

          setLastUploadState('');

          // Focus the textarea after screenshot is added
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        } else {
          setLastUploadState('');
          throw new Error('Failed to capture screenshot - no data received');
        }
      } catch (captureError) {
        if (showErrorToast) {
          toast.error('Failed to capture screenshot');
        }
        logError('Screen capture error', captureError);
        setLastUploadState('');
        throw captureError;
      }
    };

    const handlePageCapture = useCallback(async () => {
      if (!pageCaptureService.isAvailable) {
        toast.error('Page Capture Not Available', 'Page capture requires the browser extension.');
        return;
      }

      try {
        const result = await pageCaptureService.captureCurrentPage();
        actions.setPageContext(result);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Capture failed';
        toast.error('Page Capture Failed', msg);
        logError('Page capture error', err);
      }
    }, [pageCaptureService, actions]);

    const handleSubmit = async (directValue?: string) => {
      if (!state.isOnline) {
        toast.error(
          'Connection required',
          'Chat functionality requires an internet connection. Please check your connection and try again.'
        );
        return;
      }

      // Determine the message to submit
      const messageToSubmit = directValue || state.draftUserMessage;
      if (!messageToSubmit.trim()) {
        return;
      }

      // Store the message before submission for potential error recovery
      lastSubmittedMessage.current = messageToSubmit;

      // Permanently dismiss starter prompts on first message send from an empty chat
      if (!state.sessionId && !starterPromptsDismissed) {
        updateUIPreferences({ starterPromptsDismissed: true });
      }

      // Pass the message directly to submitMessage
      // Note: submitMessage already handles directMessage internally, no need to update draft
      actions.submitMessage(directValue);

      // Clear saved draft from localStorage after submission
      clearSavedDraft();
    };

    const handleClose = () => {
      // Check if there's unsaved draft content
      const hasDraft =
        state.draftUserMessage.trim() ||
        state.draftAttachments.length > 0 ||
        state.draftScreenshots.length > 0;

      if (hasDraft) {
        // Show draft save dialog
        setDraftSaveDialog({
          isOpen: true,
          pendingAction: () => {
            actions.closeChat();
            onClose?.();
          },
          actionType: 'close',
        });
      } else {
        // No draft, proceed directly
        actions.closeChat();
        onClose?.();
      }
    };

    const handleNewChat = () => {
      // Check if there's unsaved draft content
      const hasDraft =
        state.draftUserMessage.trim() ||
        state.draftAttachments.length > 0 ||
        state.draftScreenshots.length > 0;

      if (hasDraft) {
        // Show draft save dialog
        setDraftSaveDialog({
          isOpen: true,
          pendingAction: () => {
            actions.newChat();
          },
          actionType: 'new-chat',
        });
      } else {
        // No draft, proceed directly
        actions.newChat();
      }
    };

    const handleRemoveAttachment = (index: number) => {
      // The index refers to the position in the combined draftAttachments array
      const attachment = state.draftAttachments[index];
      if (!attachment) return;

      if (attachment.isScreenshot) {
        // Find the index in the screenshots array
        const screenshotIndex = state.draftScreenshots.findIndex(s => s === attachment);
        if (screenshotIndex !== -1) {
          actions.removeScreenshot(screenshotIndex);
        }
      } else {
        // Find the index in the files array
        const fileIndex = state.draftFiles.findIndex(f => f === attachment);
        if (fileIndex !== -1) {
          actions.removeAttachment(fileIndex);
        }
      }
    };

    // Derive loading states from attachment processing states
    // Files are "processing" if they're actively uploading OR if they haven't been uploaded to S3 yet
    const isProcessingFiles = state.draftAttachments.some(
      attachment => !attachment.isScreenshot && (attachment.processing || !attachment.s3Key)
    );
    const isCapturingScreen = state.draftAttachments.some(
      attachment => attachment.processing && attachment.isScreenshot
    );

    // Handle error modal actions
    const handleErrorAction = (actionId: ErrorAction) => {
      logDebug('Error action triggered:', actionId);

      switch (actionId) {
        case 'new-chat':
          // Start a new chat session
          actions.newChat();
          toast.success(
            'Fresh Chat Started',
            'All attachments and history cleared. You can start a new conversation.'
          );
          break;

        case 'remove-attachments':
          // Open smart removal dialog to let user choose which attachments to remove
          if (state.draftAttachments.length > 0 || state.draftScreenshots.length > 0) {
            setAttachmentRemovalDialog({ isOpen: true });
          } else {
            toast.info('No Attachments', 'There are no attachments to remove.');
          }
          break;

        case 'shorten-message':
          // Restore the last submitted message so user can edit it
          if (lastSubmittedMessage.current) {
            actions.updateDraftMessage(lastSubmittedMessage.current);
            toast.info(
              'Edit Your Message',
              'Your message has been restored. Please shorten it and try again.'
            );
          } else {
            toast.info('Shorten Your Message', 'Please edit your message to make it shorter.');
          }
          break;

        case 'retry':
          // Retry the last message - restore it first, then submit
          if (lastSubmittedMessage.current) {
            // Restore the message to draft
            actions.updateDraftMessage(lastSubmittedMessage.current);

            // Close the error modal
            setErrorModal({ isOpen: false, error: null });

            // Small delay to ensure state is updated before retrying
            setTimeout(() => {
              handleSubmit().catch((error: unknown) => {
                logError('Failed to retry message:', error);
                // Re-map and show the error modal again
                const errorMessage = error instanceof Error ? error.message : String(error);
                const mappedError = mapOpenAIError(errorMessage);
                setErrorModal({ isOpen: true, error: mappedError });
              });
            }, 100);
          } else if (state.draftUserMessage) {
            // Fallback: if we have a draft message, just retry with that
            setErrorModal({ isOpen: false, error: null });
            handleSubmit().catch((error: unknown) => {
              logError('Failed to retry message:', error);
              const errorMessage = error instanceof Error ? error.message : String(error);
              const mappedError = mapOpenAIError(errorMessage);
              setErrorModal({ isOpen: true, error: mappedError });
            });
          } else {
            // No message to retry
            toast.error('No Message', 'No message available to retry.');
          }
          break;

        case 'contact-support':
          // Open support link (you can customize this)
          toast.info('Contact Support', 'Please reach out to your administrator for assistance.');
          break;

        case 'upgrade-plan':
          // Navigate to billing/plans (if you have that functionality)
          toast.info('View Plans', 'Please contact your administrator to upgrade your plan.');
          break;

        default:
          logDebug('Unknown error action:', actionId);
      }
    };

    // Handle attachment removal confirmation
    const handleAttachmentRemovalConfirm = useCallback(
      (selectedIds: string[]) => {
        if (selectedIds.length === 0) {
          setAttachmentRemovalDialog({ isOpen: false });
          return;
        }

        // Remove selected attachments by filtering out those with matching IDs
        const remainingAttachments = state.draftAttachments.filter(
          att => !selectedIds.includes(att.id)
        );
        const remainingScreenshots = state.draftScreenshots.filter(
          att => !selectedIds.includes(att.id)
        );

        actions.updateAttachments(remainingAttachments);
        actions.updateScreenshots(remainingScreenshots);

        setAttachmentRemovalDialog({ isOpen: false });
        toast.success(
          'Attachments Removed',
          `${selectedIds.length} attachment(s) removed successfully.`
        );
      },
      [state.draftAttachments, state.draftScreenshots, actions]
    );

    // Restore draft from localStorage
    const restoreDraft = useCallback(() => {
      try {
        const savedDraft = localStorage.getItem('chat-draft');
        if (!savedDraft) return;

        const draft = JSON.parse(savedDraft);

        // Restore message
        if (draft.message) {
          actions.updateDraftMessage(draft.message);
        }

        // Restore attachments
        if (draft.attachments && Array.isArray(draft.attachments)) {
          actions.updateAttachments(draft.attachments);
        }

        // Restore screenshots
        if (draft.screenshots && Array.isArray(draft.screenshots)) {
          actions.updateScreenshots(draft.screenshots);
        }

        toast.success('Draft Restored', 'Your saved draft has been loaded.');
        logDebug('Draft restored successfully', {
          messageLength: draft.message?.length || 0,
          attachmentCount: draft.attachments?.length || 0,
          screenshotCount: draft.screenshots?.length || 0,
        });
      } catch (error) {
        logError('Failed to restore draft:', error);
        toast.error('Restore Failed', 'Could not restore your saved draft.');
      }
    }, [actions]);

    // Clear draft from localStorage
    const clearSavedDraft = useCallback(() => {
      try {
        localStorage.removeItem('chat-draft');
        logDebug('Saved draft cleared from localStorage');
      } catch (error) {
        logError('Failed to clear saved draft:', error);
      }
    }, []);

    // Handle draft save confirmation
    const handleDraftSaveAction = useCallback(
      async (action: DraftSaveAction) => {
        if (action === 'cancel') {
          setDraftSaveDialog({ isOpen: false, pendingAction: null, actionType: null });
          return;
        }

        if (action === 'save') {
          // Save draft to localStorage
          const draft = {
            message: state.draftUserMessage,
            attachments: state.draftAttachments,
            screenshots: state.draftScreenshots,
            timestamp: Date.now(),
          };
          localStorage.setItem('chat-draft', JSON.stringify(draft));
          toast.success('Draft Saved', 'Your message and attachments have been saved.');
        }

        // Execute pending action (new chat or close)
        if (draftSaveDialog.pendingAction) {
          await draftSaveDialog.pendingAction();
        }

        // Restore draft after starting new chat
        if (action === 'save' && draftSaveDialog.actionType === 'new-chat') {
          // Use setTimeout to ensure the new chat state has been applied
          setTimeout(() => {
            restoreDraft();
          }, 100);
        }

        setDraftSaveDialog({ isOpen: false, pendingAction: null, actionType: null });
      },
      [
        state.draftUserMessage,
        state.draftAttachments,
        state.draftScreenshots,
        draftSaveDialog.pendingAction,
        draftSaveDialog.actionType,
        restoreDraft,
      ]
    );

    // Archive/Unarchive handlers
    const handleArchive = useCallback(
      async (sessionId: string) => {
        try {
          // Optimistic update
          archiveSessionStore(sessionId);

          // API call
          await archiveChatMutation.mutateAsync({
            params: { sessionId },
            body: {},
          });

          toast.success('Chat Archived', 'Chat has been moved to archives.');
        } catch (error) {
          // Revert optimistic update
          unarchiveSessionStore(sessionId);
          toast.error('Archive Failed', 'Failed to archive chat. Please try again.');
          logError('Failed to archive chat:', error);
        }
      },
      [archiveSessionStore, unarchiveSessionStore, archiveChatMutation]
    );

    const handleUnarchive = useCallback(
      async (sessionId: string) => {
        try {
          // Optimistic update
          unarchiveSessionStore(sessionId);

          // API call
          await unarchiveChatMutation.mutateAsync({
            params: { sessionId },
            body: {},
          });

          toast.success('Chat Restored', 'Chat has been restored from archives.');
        } catch (error) {
          // Revert optimistic update
          archiveSessionStore(sessionId);
          toast.error('Restore Failed', 'Failed to restore chat. Please try again.');
          logError('Failed to unarchive chat:', error);
        }
      },
      [archiveSessionStore, unarchiveSessionStore, unarchiveChatMutation]
    );

    const handleRenameOpen = useCallback((sessionId: string, currentTitle: string) => {
      setRenameDialog({ isOpen: true, sessionId, currentTitle });
    }, []);

    // Shared rename logic — used by both the dialog and inline rename in the sidebar
    const performRename = useCallback(
      async (sessionId: string, newTitle: string) => {
        try {
          await updateTitleMutation.mutateAsync({
            params: { sessionId },
            body: { title: newTitle },
          });
          useS3ChatStore.getState().updateSession(sessionId, { title: newTitle });
          toast.success('Chat Renamed', `Chat has been renamed to "${newTitle}".`);
        } catch (error) {
          toast.error('Rename Failed', 'Failed to rename chat. Please try again.');
          logError('Failed to rename chat:', error);
          throw error;
        }
      },
      [updateTitleMutation]
    );

    const handleRenameConfirm = useCallback(
      async (newTitle: string) => {
        const { sessionId } = renameDialog;
        if (!sessionId) return;
        setRenameDialog(prev => ({ ...prev, isOpen: false }));
        try {
          await performRename(sessionId, newTitle);
        } catch {
          // Toast already shown in performRename
        }
      },
      [renameDialog, performRename]
    );

    const handleDeleteOpen = useCallback((sessionId: string, sessionTitle: string) => {
      setDeleteDialog({ isOpen: true, sessionId, sessionTitle });
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
      const { sessionId } = deleteDialog;
      if (!sessionId) return;

      try {
        // Track session deletion
        import('../../services/analytics').then(({ mixpanel }) => {
          mixpanel.track('Chat Session Deleted', {
            sessionId,
          });
        });

        // API call
        await deleteChatMutation.mutateAsync({
          params: { sessionId },
        });

        // Remove from store (this happens automatically via query invalidation, but let's be explicit)
        const store = useS3ChatStore.getState();
        store.setSessions(store.sessions.filter(s => s.id !== sessionId));

        // Clean up pinned state so a re-created session with the same ID isn't
        // accidentally pre-pinned (defensive; session IDs are uuids so this is
        // cheap insurance).
        usePinnedSessionsStore.getState().unpin(sessionId);

        // Clear current session if it was the deleted one
        if (state.sessionId === sessionId) {
          actions.closeChat();
        }

        setDeleteDialog({ isOpen: false, sessionId: '', sessionTitle: '' });
        toast.success('Chat Deleted', 'Chat has been permanently deleted.');
      } catch (error) {
        toast.error('Delete Failed', 'Failed to delete chat. Please try again.');
        logError('Failed to delete chat:', error);
      }
    }, [deleteDialog, deleteChatMutation, state.sessionId, actions]);

    // Regenerate — strip last AI+user turn and re-submit the user message
    const handleRegenerate = useCallback(async () => {
      if (!state.sessionId || !state.currentSession || state.isLoading) return;

      const msgs = state.currentSession.messages;

      // Walk back to find the last user message
      let lastUserIdx = -1;
      for (let i = msgs.length - 1; i >= 0; i--) {
        const role = (msgs[i] as any).role || (msgs[i] as any).sender;
        if (role === 'user') {
          lastUserIdx = i;
          break;
        }
      }

      if (lastUserIdx === -1) return;
      const lastUserMsg = msgs[lastUserIdx];
      if (!lastUserMsg?.content?.trim()) return;

      // Keep everything before the last user message, then re-submit it
      const messagesToKeep = msgs.slice(0, lastUserIdx);
      useS3ChatStore.getState().setSessionMessages(state.sessionId, messagesToKeep);

      await actions.submitMessage(lastUserMsg.content);
    }, [state.sessionId, state.currentSession, state.isLoading, actions]);

    // Retry — re-send a user message that failed. Removes the errored message
    // from the session first to avoid duplicates, then re-submits its content.
    const handleRetry = useCallback(
      async (messageId: string) => {
        if (!state.sessionId || !state.currentSession || state.isLoading) return;

        const msgs = state.currentSession.messages;
        const target = msgs.find(m => m.id === messageId);
        if (!target?.content?.trim()) return;

        const remaining = msgs.filter(m => m.id !== messageId);
        useS3ChatStore.getState().setSessionMessages(state.sessionId, remaining);

        await actions.submitMessage(target.content);
      },
      [state.sessionId, state.currentSession, state.isLoading, actions]
    );

    // Context shrink — compress conversation via OpenAI Compact API
    const handleShrinkContext = useCallback(async () => {
      if (!state.sessionId) return;
      const currentSession = state.currentSession;
      if (!currentSession) return;

      const chatHistory = currentSession.messages.map(msg => ({
        role: (msg.sender === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: msg.content,
      }));

      if (chatHistory.length === 0) return;

      try {
        const result = await compactContextMutation.mutateAsync({
          body: { chatHistory, sessionId: state.sessionId },
        });
        const { compactionToken, compactionMessageIndex } = result.body;

        useS3ChatStore.getState().updateSession(state.sessionId, {
          compactionToken,
          compactionMessageIndex,
        });

        toast.success(
          'Context Compressed',
          'Conversation context has been compressed. You now have more room to continue.'
        );
      } catch (error) {
        logError('Failed to compact context:', error);
        toast.error('Compression Failed', 'Could not compress context. Please try again.');
      }
    }, [state.sessionId, state.currentSession, compactContextMutation]);

    // Filter sessions based on archive filter
    const filteredSessions = React.useMemo(() => {
      return state.sessions.filter(s =>
        archiveFilter === 'archived' ? s.isArchived : !s.isArchived
      );
    }, [state.sessions, archiveFilter]);

    // Context token estimation (OpenAI GPT-4 limit: 128k tokens)
    const MAX_CONTEXT_TOKENS = 128000;
    const estimatedTokens = React.useMemo(() => {
      const allMessages = state.currentSession?.messages || [];
      // If the session has been compacted, only estimate tokens for the new messages
      // that will actually be sent to OpenAI, plus a fixed overhead for the compaction item.
      const compactionMessageIndex = state.currentSession?.compactionMessageIndex;
      const hasCompaction = state.currentSession?.compactionToken !== undefined;
      const localMessages =
        hasCompaction && compactionMessageIndex !== undefined
          ? allMessages.slice(compactionMessageIndex)
          : allMessages;
      // Convert LocalChatMessage to ChatMessage format for token estimation
      const chatHistory = localMessages.map(msg => {
        // Convert attachments: if we have metadata, use it; otherwise fall back to legacy string format
        const attached_files = msg.attachmentMetadata
          ? msg.attachmentMetadata
              .filter(meta => {
                // Filter out invalid/malformed metadata objects
                if (!meta || typeof meta !== 'object') {
                  logError('Invalid attachment metadata (not an object):', meta);
                  return false;
                }
                // Ensure required fields exist
                if (!meta.s3Key || !meta.fileName) {
                  logError('Attachment metadata missing required fields:', meta);
                  return false;
                }
                return true;
              })
              .map(meta => ({
                file_id: meta.s3Key,
                url: '', // Not needed for token estimation
                expires_at: new Date().toISOString(), // Not needed for token estimation
                file_name: meta.fileName,
                file_size_mb: meta.fileSizeMB || 0,
                estimated_tokens: meta.estimatedTokens || 0,
                content_type: meta.contentType || 'application/octet-stream',
              }))
          : msg.attachedFiles; // Legacy format: just S3 keys

        return {
          content: msg.content,
          sender: msg.sender,
          created_at: msg.timestamp,
          attached_files,
          id: msg.id,
        };
      });
      const baseEstimate = estimateTotalConversationTokens(
        state.draftUserMessage,
        [...state.draftScreenshots, ...state.draftAttachments],
        chatHistory
      );
      // Add fixed overhead to account for the compaction item in the context window
      return hasCompaction ? baseEstimate + 1000 : baseEstimate;
    }, [
      state.draftUserMessage,
      state.draftScreenshots,
      state.draftAttachments,
      state.currentSession?.messages,
      state.currentSession?.compactionToken,
      state.currentSession?.compactionMessageIndex,
    ]);

    const contextUsagePercentage = (estimatedTokens / MAX_CONTEXT_TOKENS) * 100;
    const showContextWarning = contextUsagePercentage > 80;

    return (
      <div className={cn('flex flex-1 h-full shrink-0 relative flex-col', className)}>
        <ChatGate
          className={cn(
            'flex flex-1 h-full shrink-0 relative flex-col border-border border-[1px]',
            needsFlatDesign || isSidebar ? '' : 'rounded-xl'
          )}
          onClose={onClose}
        >
          <div
            className={cn(
              'w-full h-full flex overflow-hidden bg-background',
              isSidebar ? 'rounded-none p-0 gap-0' : 'rounded-lg p-1 sm:p-3 gap-1 sm:gap-3',
              className
            )}
          >
            {showSessionPanel && (
              <div
                className={cn(
                  'flex flex-col flex-shrink-0 overflow-hidden sidebar-animate-in',
                  isSidebar
                    ? 'w-56 bg-card border-r border-border rounded-none'
                    : 'w-60 glass-card-bg glass-card-border rounded-2xl'
                )}
              >
                <SkillDynamicIsland
                  skill={selectedSkill}
                  onOpen={() => {
                    setIsSkillSelectorOpen(true);
                    import('../../services/analytics').then(({ mixpanel }) => {
                      mixpanel.track('AI Skill Selector Opened', {
                        location: 'sidebar-block',
                        currentSkillId: selectedSkill?.id ?? null,
                      });
                    });
                  }}
                />
                {/* Sidebar Header with New Chat button and Chats/Archived switcher */}
                <SidebarHeader
                  showArchived={archiveFilter === 'archived'}
                  onToggleArchived={showArchived =>
                    setArchiveFilter(showArchived ? 'archived' : 'active')
                  }
                  onNewChat={() => {
                    handleNewChat();
                    onSessionSelect?.();
                  }}
                />

                {/* Chat Session List */}
                <ChatSessionList
                  sessions={filteredSessions}
                  allSessions={state.sessions}
                  currentSessionId={state.sessionId}
                  isLoading={state.isLoading}
                  onSelectSession={id => {
                    actions.openChatSession(id);
                    onSessionSelect?.();
                  }}
                  groupByTime={true}
                  archiveFilter={archiveFilter}
                  onArchiveFilterChange={setArchiveFilter}
                  archivedCount={archivedCount}
                  onArchive={handleArchive}
                  onUnarchive={handleUnarchive}
                  onRename={handleRenameOpen}
                  onInlineRename={performRename}
                  onDelete={handleDeleteOpen}
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  searchResults={searchResults}
                  searchProgress={searchProgress}
                />
              </div>
            )}

            <div
              className={cn(
                'flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden relative',
                isSidebar ? 'rounded-none bg-background' : 'glass-card rounded-2xl'
              )}
            >
              {/* Context Usage Bar */}
              {state.sessionId && (
                <div className="px-4 pt-3 pb-2">
                  <ContextUsageBar
                    estimatedTokens={estimatedTokens}
                    onShrinkContext={handleShrinkContext}
                    isShrinking={compactContextMutation.isPending}
                  />
                </div>
              )}

              {/* Context Warning Banner */}
              {showContextWarning && state.sessionId && (
                <div className="px-4 pb-2">
                  <ContextWarningBanner
                    onStartNewChat={handleNewChat}
                    estimatedTokens={estimatedTokens}
                  />
                </div>
              )}

              {/* Real-Time Mode — floats as an absolute overlay at the top
                  of the chat column so it never displaces the chat log or
                  pushes the input off screen. The panel has its own dismiss
                  (×) button. z-10 keeps it above chat content but below
                  modals. */}
              <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none">
                <div className="pointer-events-auto">
                  <RealTimeModeView
                    {...(isAdmin && onGoToIntegrations
                      ? { onConnectIntegration: onGoToIntegrations }
                      : {})}
                    {...(isAttachableMatchState(realTimeModeMatch)
                      ? (() => {
                          const attachContext = () => {
                            if (
                              realTimeModeMatch.status !== 'matched' &&
                              realTimeModeMatch.status !== 'likely'
                            )
                              return;
                            const block = formatModmedContext(realTimeModeMatch);
                            if (block === null) return;
                            actions.setPageContext({
                              content: block,
                              title: `${providerDisplayName(realTimeModeMatch.context.provider)} — ${realTimeModeMatch.data.patient.name}`,
                              url: realTimeModeMatch.data.enterPatientUrl ?? '',
                              charCount: block.length,
                              wasTruncated: false,
                            });
                            if (textareaRef.current) textareaRef.current.focus();
                          };
                          const isModmedSpecialistActive =
                            selectedSkill?.id === 'modmed-specialist';
                          return {
                            onAttachContext: attachContext,
                            ...(isModmedSpecialistActive
                              ? {}
                              : {
                                  onUseModmedSpecialist: () => {
                                    aiSkillStore.selectSkill(
                                      sessionIdForSkill,
                                      'modmed-specialist'
                                    );
                                    attachContext();
                                  },
                                }),
                          };
                        })()
                      : {})}
                  />
                </div>
              </div>

              {/* Chat Log - always visible, shows empty state when no session */}
              <ChatLog
                ref={textareaRef}
                messages={
                  state.sessionId
                    ? mapLocalMessagesToChatMessages(state.currentSession?.messages ?? [])
                    : []
                }
                {...(!state.isLoading ? { onRegenerate: handleRegenerate } : {})}
                {...(!state.isLoading ? { onRetry: handleRetry } : {})}
                sessionId={state.sessionId} // Pass sessionId to enable bucket store image retrieval
                targetMessageId={state.targetMessageId} // For scrolling to specific message from search
                value={state.draftUserMessage}
                placeholder={
                  !state.isOnline
                    ? 'Chat disabled - No internet connection'
                    : state.isLoading
                      ? 'Analysis in progress - Please wait...'
                      : !state.sessionId
                        ? 'How can I help you?'
                        : 'Tell me about my screen'
                }
                disabled={state.isLoading || !state.isOnline}
                isLoading={state.isLoading}
                isLoadingMessages={state.currentSession?.isLoading ?? false}
                isProcessingFiles={isProcessingFiles}
                isCapturingScreen={isCapturingScreen}
                onChange={actions.updateDraftMessage}
                onSubmit={handleSubmit}
                onUpload={handleUpload}
                onCapture={screenCaptureService.isAvailable ? handleScreenCapture : undefined}
                onCapturePageHtml={pageCaptureService.isAvailable ? handlePageCapture : undefined}
                isCapturingPage={pageCaptureService.isCapturing}
                draftPageContext={state.draftPageContext}
                onClearPageContext={actions.clearPageContext}
                hasPendingScreenshot={state.draftHasScreenshot}
                attachedFiles={state.draftAttachments}
                onRemoveAttachment={handleRemoveAttachment}
                onFilesDrop={handleFilesDrop}
                onFilesPaste={handleFilesDrop} // Use same handler as drag and drop
                onClose={handleClose}
                {...(onGoToIntegrations ? { onGoToIntegrations } : {})}
                selectedSkill={selectedSkill}
                onOpenSkillSelector={() => {
                  setIsSkillSelectorOpen(true);
                  import('../../services/analytics').then(({ mixpanel }) => {
                    mixpanel.track('AI Skill Selector Opened', {
                      location: 'chat-input',
                      currentSkillId: selectedSkill?.id ?? null,
                    });
                  });
                }}
                className="flex-1"
                showEmptyState={!state.sessionId && !starterPromptsDismissed}
              />
            </div>
          </div>

          {/* AI Specialist Selector Modal — rendered here (not inside ChatLog) to
              escape the backdrop-filter stacking context of the input container */}
          <SkillSelectorModal
            isOpen={isSkillSelectorOpen}
            onClose={() => setIsSkillSelectorOpen(false)}
            skills={availableSkills}
            selectedSkillId={selectedSkill?.id ?? null}
            onRequestNewEmr={() => {
              const subject = encodeURIComponent('New EMR Integration Request');
              const body = encodeURIComponent(
                'Hi,\n\nI would like to request a new EMR integration with Ctrl Enter.\n\nEMR / System name:\nWebsite:\nAdditional context:\n'
              );
              window.open(`mailto:support@enter.health?subject=${subject}&body=${body}`, '_blank');
              import('../../services/analytics').then(({ mixpanel }) => {
                mixpanel.track('Request New EMR Integration Clicked');
              });
            }}
            onSelectSkill={skillId => {
              aiSkillStore.selectSkill(sessionIdForSkill, skillId);
              if (skillId) {
                const skill = availableSkills.find(s => s.id === skillId);
                if (skill) {
                  import('../../services/analytics').then(({ mixpanel }) => {
                    mixpanel.track('Skill Selected', {
                      skillId: skill.id,
                      skillName: skill.name,
                      skillCategory: skill.category,
                      location: 'skill-selector-modal',
                    });
                  });
                }
              }
            }}
          />

          {/* Screen Capture Permission Modal */}
          {permissionGuard.showPermissionModal && (
            <ScreenCapturePermissionModal onClose={permissionGuard.closePermissionModal} />
          )}

          {/* Chat Error Modal */}
          {errorModal.isOpen && errorModal.error && (
            <ChatErrorModal
              isOpen={errorModal.isOpen}
              onClose={() => setErrorModal({ isOpen: false, error: null })}
              error={errorModal.error}
              onAction={actionId => handleErrorAction(actionId)}
            />
          )}

          {/* Delete Confirmation Dialog */}
          <DeleteChatDialog
            isOpen={deleteDialog.isOpen}
            sessionTitle={deleteDialog.sessionTitle}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteDialog({ isOpen: false, sessionId: '', sessionTitle: '' })}
            isDeleting={deleteChatMutation.isPending}
          />

          {/* Rename Dialog */}
          <RenameChatDialog
            isOpen={renameDialog.isOpen}
            currentTitle={renameDialog.currentTitle}
            onConfirm={handleRenameConfirm}
            onCancel={() => setRenameDialog({ isOpen: false, sessionId: '', currentTitle: '' })}
            isRenaming={updateTitleMutation.isPending}
          />

          {/* Attachment Removal Dialog */}
          <AttachmentRemovalDialog
            isOpen={attachmentRemovalDialog.isOpen}
            onCancel={() => setAttachmentRemovalDialog({ isOpen: false })}
            attachments={[...state.draftAttachments, ...state.draftScreenshots]}
            onConfirm={handleAttachmentRemovalConfirm}
          />

          {/* Draft Save Dialog */}
          <DraftSaveDialog
            isOpen={draftSaveDialog.isOpen}
            draftMessage={state.draftUserMessage}
            draftAttachments={state.draftAttachments}
            actionType={draftSaveDialog.actionType || 'close'}
            onAction={handleDraftSaveAction}
          />
        </ChatGate>
      </div>
    );
  }
);

ChatView.displayName = 'ChatView';
